import {
  AtSignIcon,
  BanIcon,
  CalendarClockIcon,
  CalendarX2Icon,
  CircleCheckIcon,
  CircleStopIcon,
  CircleXIcon,
  CoinsIcon,
  DoorOpenIcon,
  GaugeIcon,
  GitForkIcon,
  KeyRoundIcon,
  ListStartIcon,
  ListChecksIcon,
  MessageSquareIcon,
  MessageSquareReplyIcon,
  SirenIcon,
  UnplugIcon,
  UserCheckIcon,
  UserMinusIcon,
  UserPlusIcon,
  UserXIcon,
  UsersIcon,
  ZapOffIcon,
  type LucideIcon,
} from 'lucide-react';

import type {
  NotificationKind,
  NotificationSeverity,
  NotificationView,
} from '@/lib/api/generated/model';

/**
 * What each kind looks like in the inbox (spec 141 US1): one icon per kind,
 * so a reader tells a spend stop from a comment before reading a word.
 * Exhaustive, so a new kind cannot ship without one.
 */
export const KIND_ICON: Record<NotificationKind, LucideIcon> = {
  gate_reached: UserCheckIcon,
  gate_assigned: UserCheckIcon,
  task_failed: CircleXIcon,
  task_blocked: BanIcon,
  task_stopped: CircleStopIcon,
  child_needs_attention: GitForkIcon,
  task_done: CircleCheckIcon,
  children_finished: ListChecksIcon,
  task_commented: MessageSquareIcon,
  task_mentioned: AtSignIcon,
  spend_limit_reached: CoinsIcon,
  spend_limit_approaching: GaugeIcon,
  trigger_failed: ZapOffIcon,
  connection_failed: UnplugIcon,
  escalated: SirenIcon,
  linked_tasks_waiting: ListStartIcon,
  // The scheduler's own outcomes (spec 239 US3).
  scheduler_failed: CalendarX2Icon,
  scheduler_stalled: CalendarClockIcon,
  run_replied: MessageSquareReplyIcon,
  // The person's own linked forge account (spec 159 US2).
  forge_account_broken: KeyRoundIcon,
  forge_account_expiring: CalendarClockIcon,
  forge_account_revoked: KeyRoundIcon,
  forge_account_required: KeyRoundIcon,
  forge_account_requested: KeyRoundIcon,
  // A workspace's people (spec 187).
  workspace_member_added: UserPlusIcon,
  workspace_member_removed: UserMinusIcon,
  workspace_access_requested: DoorOpenIcon,
  workspace_without_owner: UserXIcon,
  workspace_members_review: UsersIcon,
};

export type Tone = 'danger' | 'primary' | 'warning' | 'success' | 'muted';

const SEVERITY_TONE: Record<NotificationSeverity, Tone> = {
  critical: 'danger',
  action_required: 'primary',
  warning: 'warning',
  informational: 'muted',
};

/** Outcomes that went well read as such, whatever their severity says. */
const SUCCESS_KINDS: ReadonlySet<NotificationKind> = new Set([
  'task_done',
  'children_finished',
]);

/** The square's tint: its severity, or success for a finished outcome. */
export function toneOf(notification: NotificationView): Tone {
  if (SUCCESS_KINDS.has(notification.kind)) return 'success';
  return SEVERITY_TONE[notification.severity];
}

/** The square's classes per tone: the theme's state tokens, so both themes follow. */
export const TONE_CLASS: Record<Tone, string> = {
  danger: 'bg-destructive/10 text-destructive',
  primary: 'bg-primary/10 text-primary',
  warning: 'bg-fill-warning/25 text-state-warning',
  success: 'bg-fill-success/20 text-state-success',
  muted: 'bg-muted text-muted-foreground',
};
